import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, XCircle, AlertTriangle, RefreshCw, 
  Code, Database, Globe, CreditCard, Share2, Bot,
  TrendingUp, Clock, Shield, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CodeAuditor = () => {
  const [loading, setLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/audit/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
    } catch (e) {
      console.error('Error loading history:', e);
    }
  };

  const runAudit = async () => {
    setLoading(true);
    toast.info('🔍 Iniciando análise completa do sistema...');
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/audit/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditResult(res.data);
      loadHistory();
      toast.success('✅ Análise concluída!');
    } catch (e) {
      toast.error('Erro ao executar auditoria');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-600';
    if (score >= 60) return 'from-yellow-500 to-orange-600';
    return 'from-red-500 to-pink-600';
  };

  const CategoryCard = ({ title, icon: Icon, items, color }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4" style={{ borderColor: color }}>
      <div className="flex items-center gap-3 mb-4">
        <Icon className="h-6 w-6" style={{ color }} />
        <h3 className="font-bold text-lg text-gray-800">{title}</h3>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            {item.status === 'ok' && <CheckCircle className="h-4 w-4 text-green-500" />}
            {item.status === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
            {item.status === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
            <span className={item.status === 'error' ? 'text-red-600' : 'text-gray-700'}>
              {item.name}
            </span>
            {item.detail && <span className="text-gray-400 text-xs">({item.detail})</span>}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Code className="h-8 w-8 text-purple-600" />
          Auditor de Código Inteligente
        </h1>
        <p className="text-gray-600 mt-2">Análise completa do sistema com IA</p>
      </div>

      {/* Run Audit Button */}
      <div className="mb-8">
        <button
          onClick={runAudit}
          disabled={loading}
          className={`flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-white text-lg transition-all ${
            loading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
          }`}
        >
          <RefreshCw className={`h-6 w-6 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Analisando...' : '🔍 Executar Auditoria Completa'}
        </button>
      </div>

      {/* Results */}
      {auditResult && (
        <>
          {/* Health Score */}
          <div className={`bg-gradient-to-r ${getScoreBg(auditResult.health_score)} rounded-2xl p-8 text-white mb-8 shadow-xl`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">🎯 APP HEALTH SCORE</h2>
                <div className="flex items-baseline gap-2">
                  <span className="text-7xl font-black">{auditResult.health_score}</span>
                  <span className="text-3xl">/100</span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-lg">Funcionando: {auditResult.summary.working} componentes</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="text-lg">Atenção: {auditResult.summary.warnings} pendências</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  <span className="text-lg">Crítico: {auditResult.summary.errors} problemas</span>
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="w-full bg-white/20 rounded-full h-4">
                <div 
                  className="bg-white rounded-full h-4 transition-all duration-1000"
                  style={{ width: `${auditResult.health_score}%` }}
                />
              </div>
              <p className="text-sm mt-2 opacity-90">{auditResult.health_score}% do sistema operacional</p>
            </div>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <CategoryCard 
              title="Integrações de Afiliados" 
              icon={Share2} 
              items={auditResult.categories.affiliates}
              color="#8B5CF6"
            />
            <CategoryCard 
              title="Redes Sociais" 
              icon={Globe} 
              items={auditResult.categories.social}
              color="#3B82F6"
            />
            <CategoryCard 
              title="Pagamentos" 
              icon={CreditCard} 
              items={auditResult.categories.payments}
              color="#10B981"
            />
            <CategoryCard 
              title="IA & Automação" 
              icon={Bot} 
              items={auditResult.categories.ai}
              color="#F59E0B"
            />
            <CategoryCard 
              title="Backend & APIs" 
              icon={Database} 
              items={auditResult.categories.backend}
              color="#EF4444"
            />
            <CategoryCard 
              title="Segurança" 
              icon={Shield} 
              items={auditResult.categories.security}
              color="#6366F1"
            />
          </div>

          {/* Recommendations */}
          {auditResult.recommendations && auditResult.recommendations.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
              <h3 className="font-bold text-lg text-yellow-800 mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Recomendações da IA
              </h3>
              <ul className="space-y-2">
                {auditResult.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-yellow-700">
                    <span className="font-bold">{idx + 1}.</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-500" />
            Histórico de Auditorias
          </h3>
          <div className="space-y-3">
            {history.slice(0, 5).map((audit, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <TrendingUp className={`h-5 w-5 ${getScoreColor(audit.score)}`} />
                  <span className="text-gray-600">{new Date(audit.date).toLocaleString('pt-BR')}</span>
                </div>
                <div className={`font-bold text-lg ${getScoreColor(audit.score)}`}>
                  {audit.score}/100
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeAuditor;
